import React, { useCallback, useMemo, useState } from 'react';
import { useGetOverviews } from 'api/hooks/useGetOverviews';
import { usePostInvoicesStatus } from 'api/hooks/usePostInvoicesStatus';
import { Button, Icon, Modal } from 'ui';
import { LayoutWrapper, LayoutHeader, LayoutContent } from 'features/Layout';
import { SummaryCard, ActionItems } from 'features/Layout/PageActions';
import { type IInvoiceStatusPayload } from 'api/resources/invoices';
import type {
	TInvoiceStatuses,
	TPaymentMethods,
	IInvoices,
} from 'api/resources/overviews';
import { EPaymentMethod } from 'ui/types/core';
import { motion } from 'framer-motion';

import { useStackAnimation } from 'app/hooks/useStackAnimation';

import { PaymentModal } from './partials/PaymentModal';
import { CustomModalTitle } from './partials/CustomModalTitle';
import { InvoiceCard } from './partials/InvoiceCard';
import { SuspenseWrapper } from 'app/features/SuspenseWrapper/SuspenseWrapper';
import { EStatus } from 'ui/types/core';

export const Invoices = () => {
	const { data, isLoading, error } = useGetOverviews();
	const { mutate: postInvoiceStatus } = usePostInvoicesStatus();
	const [selectedInvoices, setSelectedInvoices] = useState<IInvoices[]>([]);
	const [paymentMethod, setPaymentMethod] = useState<TPaymentMethods>(
		EPaymentMethod.BankTransfer,
	);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const invoices = useMemo(() => data?.data?.invoices || [], [data]);

	const motionProps = useStackAnimation(0.1, !isLoading);

	const toggleSelection = useCallback(
		(id: string) => {
			setSelectedInvoices((prev) => {
				const invoice = invoices.find((invoice) => invoice.id === id);
				if (invoice) {
					const isSelected = prev.some((i) => i.id === id);

					if (isSelected) {
						const newSelection = prev.filter((i) => i.id !== id);

						return newSelection;
					} else {
						const newSelection = [...prev, invoice];

						return newSelection;
					}
				}

				return prev;
			});
		},
		[invoices, setSelectedInvoices],
	);

	if (error) return <div>Error loading invoices: {error.message}</div>;

	const combinedTotal = selectedInvoices.reduce(
		(sum, invoice) => sum + invoice.total,
		0,
	);
	const previousPayments = 10000;
	const balanceDue = combinedTotal - previousPayments;

	const summaryItems = [
		{ label: 'Combined Total', amount: combinedTotal },
		{ label: 'Previous Payments', amount: previousPayments },
		{ label: 'Balance Due', amount: balanceDue },
	];

	const handlePayNow = (id: string) => {
		const invoice = invoices.find((invoice) => invoice.id === id);
		if (invoice) {
			setSelectedInvoices([invoice]);
			setIsModalOpen(true);
		}
	};

	const handleModalAction = () => {
		if (selectedInvoices.length > 0) {
			const payloads = selectedInvoices.map(
				(invoice): IInvoiceStatusPayload => ({
					id: invoice.id,
					status: EStatus.Paid as TInvoiceStatuses,
					paymentMethod,
				}),
			);
			postInvoiceStatus(payloads);
			setSelectedInvoices([]);
			setPaymentMethod(EPaymentMethod.BankTransfer);
			setIsModalOpen(false);
		}
	};

	const handleOpenPdf = (url: string) => {
		window.open(url, '_blank');
	};

	return (
		<LayoutWrapper>
			<LayoutHeader
				title="Invoices"
				icon="inbox-arrow-down"
				actions={
					<Button
						variant="primary"
						className="w-32 !py-1 flex gap-2"
						onClick={() => setIsModalOpen(true)}
						disabled={selectedInvoices.length === 0}
					>
						<Icon name="credit-card" className="fill-white" />
						Pay All
					</Button>
				}
			/>
			<LayoutContent className="px-4 lg:px-20 pt-6 lg:pt-25">
				<SuspenseWrapper>
					<SummaryCard items={summaryItems} isDataReady={!isLoading} />
					<ActionItems title="Invoices to Review">
						{invoices.map((invoice, index) => (
							<motion.div
								key={index}
								initial={motionProps.initial}
								animate={motionProps.animate}
								custom={index}
								className="w-full"
							>
								<InvoiceCard
									invoice={invoice}
									isSelected={selectedInvoices.some((i) => i.id === invoice.id)}
									onSelect={toggleSelection}
									onOpenPdf={handleOpenPdf}
									onPayNow={handlePayNow}
								/>
							</motion.div>
						))}
					</ActionItems>
					{isModalOpen && (
						<Modal
							isOpen={isModalOpen}
							onClose={() => setIsModalOpen(false)}
							overrides={{
								Title: CustomModalTitle,
							}}
							showCloseButton={false}
						>
							<PaymentModal
								paymentMethod={paymentMethod}
								setPaymentMethod={setPaymentMethod as (method: string) => void}
								totalAmount={combinedTotal}
							/>
							<div className="w-full flex gap-1 justify-between mt-6">
								<Button
									variant="primary"
									onClick={handleModalAction}
									className="w-full"
								>
									<div className="flex items-center gap-1">
										<Icon name="credit-card" className="fill-white" />
										Pay Deposit
									</div>
								</Button>
							</div>
						</Modal>
					)}
				</SuspenseWrapper>
			</LayoutContent>
		</LayoutWrapper>
	);
};
